var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import usePreviousDistinct from "react-use/lib/usePreviousDistinct";
import { StyledInput, StyledLabel, InputContainer, SuccessIcon, IconContainer, StyledContainer, MetaContainer, MetaInfo, FieldInfo, LabelRow, ErrorIcon, } from "./styled";
import { useFocusedState } from "./use-focused-state";
import useInputStyles from "./use-input-styles";
var defaultHandleMetaDisplay = function (_a) {
    var isDirty = _a.isDirty, instantFeedback = _a.instantFeedback, value = _a.value, prevValue = _a.prevValue, error = _a.error, success = _a.success, touched = _a.touched;
    return touched ||
        Boolean(instantFeedback === "all" && isDirty) ||
        Boolean(instantFeedback === "positiveFirst" && isDirty && success) ||
        Boolean(instantFeedback === "positiveFirst" &&
            isDirty &&
            error &&
            prevValue &&
            value.length < prevValue.length);
}; // if user starts to erase entered data, we provide negative feedback
export var TextInput = function (_a) {
    var error = _a.error, success = _a.success, touched = _a.touched, disabled = _a.disabled, instantFeedback = _a.instantFeedback, iconLeft = _a.iconLeft, iconRight = _a.iconRight, name = _a.name, onFocus = _a.onFocus, onBlur = _a.onBlur, className = _a.className, fieldMessage = _a.fieldMessage, fieldIndicator = _a.fieldIndicator, metaShrinked = _a.metaShrinked, _b = _a.placeholder, placeholder = _b === void 0 ? "" : _b, label = _a.label, isDirty = _a.isDirty, value = _a.value, inputRef = _a.inputRef, _c = _a.size, size = _c === void 0 ? "large" : _c, _d = _a.handleMetaDisplay, handleMetaDisplay = _d === void 0 ? defaultHandleMetaDisplay : _d, props = __rest(_a, ["error", "success", "touched", "disabled", "instantFeedback", "iconLeft", "iconRight", "name", "onFocus", "onBlur", "className", "fieldMessage", "fieldIndicator", "metaShrinked", "placeholder", "label", "isDirty", "value", "inputRef", "size", "handleMetaDisplay"]);
    var _e = useFocusedState({ onBlur: onBlur, onFocus: onFocus }), focused = _e[0], handleFocus = _e[1], handleBlur = _e[2];
    var prevValue = usePreviousDistinct(value);
    var metaDisplayed = handleMetaDisplay({
        isDirty: isDirty,
        instantFeedback: instantFeedback,
        value: value,
        prevValue: prevValue,
        error: error,
        success: success,
        touched: touched,
        focused: focused,
    });
    var isSuccess = metaDisplayed && success;
    var isError = metaDisplayed && error;
    var errorMessage = isError && error !== true && error;
    var successMessage = isSuccess && success !== true && success;
    var styles = useInputStyles({
        size: size,
        error: isError,
        success: isSuccess,
        disabled: disabled,
        focused: focused,
    }).styles;
    var LeftIcon = React.useCallback(function (_a) {
        var icon = _a.icon;
        return (React.createElement(IconContainer, __assign({}, styles.iconContainer({ iconLeft: true })), icon));
    }, []);
    var RightIcon = React.useCallback(function (_a) {
        var icon = _a.icon;
        return (React.createElement(IconContainer, __assign({}, styles.iconContainer({ iconRight: true })), icon));
    }, []);
    return (React.createElement(StyledContainer, { className: className },
        React.createElement(StyledLabel, { disabled: disabled },
            label && (React.createElement(LabelRow, null,
                React.createElement("span", null, label))),
            React.createElement(InputContainer, __assign({}, styles.inputContainer, { focused: focused, success: isSuccess, error: isError, disabled: disabled }),
                iconLeft && React.createElement(LeftIcon, { icon: iconLeft }),
                React.createElement(StyledInput, __assign({}, props, { disabled: disabled, placeholder: placeholder, onBlur: handleBlur, onFocus: handleFocus, name: name, "aria-label": name, iconLeft: iconLeft, iconRight: iconRight, type: "text", value: value, ref: inputRef })),
                iconRight && React.createElement(RightIcon, { icon: iconRight }),
                metaDisplayed && error && React.createElement(RightIcon, { icon: React.createElement(ErrorIcon, { name: "cross_s" }) }),
                metaDisplayed && success && React.createElement(RightIcon, { icon: React.createElement(SuccessIcon, { name: "checkmark_s" }) }))),
        !metaShrinked && (React.createElement(MetaContainer, { margin: [1, 0, 0, 0], width: "100%", justifyContent: "between", alignItems: "center" },
            React.createElement(FieldInfo, { success: isSuccess, error: isError }, (isError && errorMessage) || (isSuccess && successMessage) || fieldMessage),
            React.createElement(MetaInfo, null, fieldIndicator)))));
};
